// Utility for handling chunk load retries
export const setupChunkRetry = () => {
  if (typeof window === "undefined") {
    return;
  }

  // Store original error handler
  const originalOnError = window.onerror;

  window.onerror = (message, source, lineno, colno, error) => {
    // Check if this is a chunk loading error
    if (
      typeof message === "string" &&
      (message.includes("Loading chunk") ||
        message.includes("Loading CSS chunk") ||
        message.includes("ChunkLoadError"))
    ) {
      console.warn("Chunk loading failed, attempting to reload page:", message);

      // Reload the page after a short delay to allow for cleanup
      setTimeout(() => {
        window.location.reload();
      }, 100);

      return true; // Prevent default error handling
    }

    // Call original error handler for other errors
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }

    return false;
  };

  // Handle unhandled promise rejections that might be chunk related
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;

    if (
      error &&
      typeof error.message === "string" &&
      (error.message.includes("Loading chunk") ||
        error.message.includes("ChunkLoadError"))
    ) {
      console.warn(
        "Unhandled chunk loading rejection, attempting to reload page:",
        error.message,
      );

      event.preventDefault(); // Prevent default error logging

      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  });
};

// Dynamic import with retry logic
export const dynamicImportWithRetry = async <T = any>(
  importFn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000,
): Promise<T> => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await importFn();
    } catch (error: any) {
      const isChunkError =
        error.message?.includes("Loading chunk") ||
        error.message?.includes("ChunkLoadError") ||
        error.name === "ChunkLoadError";

      if (isChunkError && attempt < retries) {
        console.warn(
          `Chunk loading failed (attempt ${attempt}/${retries}), retrying...`,
        );

        // Exponential backoff
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
        continue;
      }

      throw error;
    }
  }

  throw new Error(`Failed to load chunk after ${retries} attempts`);
};
